import { EvilIcons } from "@expo/vector-icons";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  FlatList,
  Pressable,
  ScrollView,
  TextInput,
} from "react-native";

interface InputDropListProps {
  label: string;
  name: string;
  control: any;
  data: { value: string; code: string; uniqueKey: string }[];
  rules?: object;
  placeholder?: string;
  handleChange?: (data: { value: string; code: string }) => void;
  onSearchChange?: (term: string) => void;
  optional?: boolean;
  clearValueFunction?: () => void;
}

interface ListItem {
  value: string;
  code: string;
  uniqueKey: string;
}
// Define the type for the handleSelectValue function
type HandleSelectValue = (item: ListItem) => void;

interface ListItemComponentProps {
  item: ListItem;
  handleSelectValue: HandleSelectValue;
}

export default function InputDropList(props: InputDropListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isListVisible, setIsListVisible] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isPointered, setIsPointered] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    //Remove spaces and convert to lowercase
    const formattedSearchQuery = searchQuery.toLowerCase().replace(/\s/g, "");
    if (formattedSearchQuery === "unitedstates") {
      handleSelectValue({
        value: "United States",
        code: "US",
        uniqueKey: "UnitedStates-US",
      });
    }
    if (formattedSearchQuery === "estadosunidos") {
      handleSelectValue({
        value: "Estados Unidos",
        code: "US",
        uniqueKey: "UnitedStates-US",
      });
    }
  }, [searchQuery]);

  useEffect(() => {
    if (props.data.length === 1) {
      handleSelectValue(props.data[0]);
    }
  }, [props.data]);

  const handleSelectValue: HandleSelectValue = (item: ListItem) => {
    if (props.data.length === 1) {
      props.handleChange?.({
        value: item.value,
        code: item.code,
      });
      setSearchQuery(
        props.name === "naics" ? item.code + " - " + item.value : item.value,
      );
    }
    props.handleChange?.({ value: item.value, code: item.code });
    setSearchQuery(
      props.name === "naics" ? item.code + " - " + item.value : item.value,
    );
  };

  const ListItemComponent: React.FC<ListItemComponentProps> = ({
    item,
    handleSelectValue,
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <ScrollView>
        <Pressable
          className={`p-4 ${isHovered ? "bg-gray" : "bg-white1"}`}
          onPressIn={() => {
            setIsTouched(true);
          }}
          onPressOut={() => {
            handleSelectValue(item);
            setIsPointered(false);
            setIsListVisible(false);
            setIsTouched(false);
          }}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
        >
          {props.name === "naics" ? (
            <Text className="text-black">{item.code + " - " + item.value}</Text>
          ) : (
            <Text className="text-black">{item.value}</Text>
          )}
        </Pressable>
      </ScrollView>
    );
  };

  return (
    <View className="z-50">
      {props.optional === true ? (
        <View className="flex-row m-2">
          <Text className="text-black text-sm">{props.label}</Text>
          <Text className="text-midgray text-sm italic">{` (${t(
            "common:placeholder.optional",
          )})`}</Text>
        </View>
      ) : (
        <Text className="text-black text-sm m-2">{props.label}</Text>
      )}
      <Controller
        control={props.control}
        name={props.name}
        rules={props.rules}
        defaultValue=""
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <>
            <View>
              <View className="items-center">
                <EvilIcons
                  name="search"
                  size={22}
                  color="#BDBDBD"
                  className="z-50 absolute top-4 left-3"
                />
                <TextInput
                  ref={ref}
                  className={`bg-white1 border text-sm border-solid rounded-xl items-center py-6 pl-10 w-80 h-14 text-black1 ${
                    error ? "border-red" : "border-midgray"
                  } ${
                    props.data.length > 0 && isListVisible
                      ? "rounded-b-none border-b-0 rounded-tr-xl rounded-tl-xl"
                      : ""
                  }`}
                  value={value ? searchQuery : ""}
                  onChangeText={(query) => {
                    props.onSearchChange?.(query);
                    onChange(query);
                    setSearchQuery(query);
                    if (props.data.length === 1) {
                      handleSelectValue(props.data[0]);
                    }
                  }}
                  onFocus={() => {
                    setIsListVisible(true);
                  }}
                  onBlur={() => {
                    if (!isPointered && !isTouched) {
                      setIsListVisible(false);
                    }
                  }}
                  placeholder={props.placeholder}
                  placeholderTextColor="#BDBDBD"
                />
                <Pressable
                  className="z-40 absolute top-4 right-3"
                  onPress={() => {
                    setSearchQuery("");
                    onChange("");
                    props.onSearchChange?.("");
                    props.handleChange?.({ value: "", code: "" });
                    props.clearValueFunction?.();
                  }}
                >
                  <EvilIcons
                    name="close"
                    size={24}
                    color="#BDBDBD"
                    className=""
                  />
                </Pressable>
              </View>
              <View style={{ height: 20, overflow: "hidden" }}>
                {error && (
                  <Text className="text-red text-xs pl-2">{error.message}</Text>
                )}
              </View>
              {props.data.length > 0 && isListVisible && (
                <FlatList
                  data={props.data}
                  renderItem={({ item }) => (
                    <ListItemComponent
                      item={item}
                      handleSelectValue={handleSelectValue}
                    />
                  )}
                  keyExtractor={(item) => item.uniqueKey}
                  className={`absolute top-14 w-80 max-h-36 bg-white1 border border-midgray rounded-bl-xl ${
                    props.data.length <= 0 || !isListVisible ? "hidden" : ""
                  } ${props.data.length <= 3 ? "rounded-br-xl" : ""}`}
                  onPointerEnter={() => setIsPointered(true)}
                  onPointerLeave={() => setIsPointered(false)}
                />
              )}
            </View>
          </>
        )}
      />
    </View>
  );
}
